'use client'

import { usePathname } from 'next/navigation'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

interface HeaderNavigationContextType {
  colorInverse: boolean
  setColorInverse: (inverse: boolean) => void
  slug: string
  isMediaPage: boolean
  isDropdownOpen: boolean
  setIsDropdownOpen: (open: boolean) => void
  isMenuOpen: boolean
  setIsMenuOpen: (open: boolean) => void
  dropdownRef: React.RefObject<HTMLDivElement | null>
  activeCallout: number | null
  setActiveCallout: (callout: number | null) => void
}

const HeaderNavigationContext = createContext<HeaderNavigationContextType | undefined>(undefined)

export const useHeaderNavigationContext = () => {
  const context = useContext(HeaderNavigationContext)
  if (!context) {
    throw new Error('useHeaderNavigationContext must be used within a HeaderNavigationProvider')
  }
  return context
}

// Utility function to calculate scrollbar width
const getScrollbarWidth = () => {
  const outer = document.createElement('div')
  outer.style.visibility = 'hidden'
  outer.style.overflow = 'scroll'
  document.body.appendChild(outer)

  const inner = document.createElement('div')
  outer.appendChild(inner)

  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth
  outer.parentNode?.removeChild(outer)

  return scrollbarWidth
}

interface HeaderNavigationProviderProps {
  children: React.ReactNode
}

const DROPDOWN_CLOSE_DELAY = 100

export const HeaderNavigationProvider: React.FC<HeaderNavigationProviderProps> = ({ children }) => {
  const [isDropdownOpen, setIsDropdownOpenRaw] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [colorInverseOverride, setColorInverseOverride] = useState<boolean | null>(null)
  const [activeCallout, setActiveCallout] = useState<number | null>(null)

  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setIsDropdownOpen = useCallback((open: boolean) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    if (open) {
      setIsDropdownOpenRaw(true)
    } else {
      closeTimeoutRef.current = setTimeout(() => {
        setIsDropdownOpenRaw(false)
        closeTimeoutRef.current = null
      }, DROPDOWN_CLOSE_DELAY)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  const pathname = usePathname() ?? ''

  // Memoized function to handle menu open state and body overflow
  const setMenuOpen = useCallback((isOpen: boolean) => {
    const htmlElement = document.documentElement
    if (isOpen) {
      const scrollbarWidth = getScrollbarWidth()
      htmlElement.style.paddingRight = `${scrollbarWidth}px`
      htmlElement.classList.add('scrollbar-hidden')
    } else {
      htmlElement.style.paddingRight = '0px'
      htmlElement.classList.remove('scrollbar-hidden')
    }
  }, [])

  // Update body overflow when menu state changes
  useEffect(() => {
    setMenuOpen(isMenuOpen)
  }, [isMenuOpen, setMenuOpen])

  const slug = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    return segments[segments.length - 1] || 'home'
  }, [pathname])

  // const isHomePage = useMemo(() => pathname === '/', [pathname])
  const isMediaPage = useMemo(() => pathname === '/media-hub', [pathname])

  // Reset scroll-based override when navigating (adjust state during render pattern)
  const [prevPathname, setPrevPathname] = useState(pathname)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname)
    setColorInverseOverride(null)
  }

  // colorInverse is only true on media page; scroll handlers can override (true/false) there
  const colorInverse = isMediaPage ? (colorInverseOverride ?? true) : false
  const setColorInverse = setColorInverseOverride

  const value: HeaderNavigationContextType = {
    colorInverse,
    setColorInverse,
    slug,
    isMediaPage,
    isDropdownOpen,
    setIsDropdownOpen,
    isMenuOpen,
    setIsMenuOpen,
    dropdownRef,
    activeCallout,
    setActiveCallout,
  }

  return (
    <HeaderNavigationContext.Provider value={value}>{children}</HeaderNavigationContext.Provider>
  )
}
